import { Box, Tooltip } from '@mui/material';
import { useRef, useState } from 'react';

export interface TruncatedLabelProps {
  /** The text to render on a single line. */
  text: string;
  /**
   * Suppresses the tooltip regardless of overflow. The account card opens a
   * Menu anchored to the same element, so the tooltip is muted while it's open
   * rather than fighting the menu for a frame.
   */
  suppressTooltip?: boolean;
}

/**
 * Single-line text that ellipsises when it runs out of room, and reveals the
 * full value in a tooltip only when it actually overflowed. Overflow is
 * measured on hover so it stays correct as the container resizes (nav collapse,
 * viewport changes) without a ResizeObserver.
 */
export function TruncatedLabel({ text, suppressTooltip }: TruncatedLabelProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const [overflowing, setOverflowing] = useState(false);

  return (
    <Tooltip
      title={overflowing && !suppressTooltip ? text : ''}
      placement="top"
    >
      <Box
        component="span"
        ref={ref}
        onMouseEnter={() => {
          const element = ref.current;
          setOverflowing(
            !!element && element.scrollWidth > element.clientWidth,
          );
        }}
        sx={{
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {text}
      </Box>
    </Tooltip>
  );
}
